import {
  loadLocalHistoryForAccount,
  saveLocalHistoryForAccount,
  type LocalHistoryStorage,
} from "./local-history.ts";
import type { HistoryItem } from "./types.ts";

export function mergeBacktestHistory(primary: HistoryItem[], secondary: HistoryItem[], limit: number): HistoryItem[] {
  const seenIDs = new Set<string>();
  const seenRunIDs = new Set<string>();
  return [...primary, ...secondary]
    .filter((item) => {
      const runID = item.result?.run_id || null;
      if (seenIDs.has(item.id) || (runID && seenRunIDs.has(runID))) return false;
      seenIDs.add(item.id);
      if (runID) seenRunIDs.add(runID);
      return true;
    })
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
    .slice(0, limit);
}

interface BacktestCompletionOptions {
  item: HistoryItem;
  accountID: string | null;
  storage: LocalHistoryStorage;
  limit: number;
  isCurrent: () => boolean;
  publishLocal: (history: HistoryItem[]) => void;
  synchronize?: () => Promise<{ items?: HistoryItem[] }>;
  publishSynchronizedHistory: (history: HistoryItem[]) => void;
}

/** A completed result is usable immediately; cloud history is optional follow-up work. */
export function completeBacktestAndSync(options: BacktestCompletionOptions): { synchronization: Promise<void> } {
  let stored: HistoryItem[];
  try {
    stored = loadLocalHistoryForAccount(options.storage, options.accountID, options.limit);
  } catch {
    // A blocked convenience cache must not hide a completed backend result.
    stored = [];
  }
  const history = mergeBacktestHistory([options.item], stored, options.limit);
  saveLocalHistoryForAccount(options.storage, options.accountID, history, options.limit);
  if (options.isCurrent()) options.publishLocal(history);

  const synchronization = Promise.resolve().then(async () => {
    if (!options.synchronize || !options.isCurrent()) return;
    const response = await options.synchronize();
    if (!options.isCurrent()) return;
    // Another run, account transition or tab may have updated the cache while
    // the cloud request was pending. Never merge into the captured old list.
    const current = loadLocalHistoryForAccount(options.storage, options.accountID, options.limit);
    if (!current.some((item) => item.id === options.item.id)) return;
    const merged = mergeBacktestHistory(current, response.items || [], options.limit);
    saveLocalHistoryForAccount(options.storage, options.accountID, merged, options.limit);
    if (options.isCurrent()) options.publishSynchronizedHistory(merged);
  }).catch(() => {
    // Local success survives sync failures; account bootstrap can retry later.
  });
  return { synchronization };
}
